import React, { useEffect, useState } from 'react'
import { Container, Row, Col, Card, Nav } from 'react-bootstrap'
import { useTranslation } from "react-i18next"
import { useAuth } from '../../components/auth/useAuth'
import { useDispatch, useSelector } from 'react-redux'
import { getDashboardMe, getStudentInvoices, getNotifications } from '../../actions/usersActions'
import { Link } from 'react-router-dom'
import AxiosControl from '../../components/axios/AxiosControl'
const Axios = AxiosControl()

const DataValue = ({ value, error, loading }) => {
    if (loading) return <div className="spinner-border spinner-border-sm text-secondary" role="status" />
    if (error || value === null || value === undefined) return <h5 className='fw-bold text-black mb-0'>&mdash;</h5>
    return <p>{value}</p>
}

export default function Dashboard() {
    const { t } = useTranslation()
    const { clientauth } = useAuth()
    const dispatch = useDispatch()

    const { dashboard, loading, dashboardError, invoices, invoicesLoading, notifications, notificationsLoading, unreadCount } = useSelector(state => state.users)
    const [activeStudentIdx, setActiveStudentIdx] = useState(0)

    const students = dashboard?.students || []
    const currentStudent = students[activeStudentIdx] || null

    useEffect(() => {
        window.scrollTo({
            top: 0,
            behavior: 'instant',
        })
        if (!dashboard) dispatch(getDashboardMe())
        dispatch(getNotifications())
    }, [dispatch, dashboard])

    useEffect(() => {
        if (currentStudent?.odooId && currentStudent?.partner && !currentStudent?.hasTutor) {
            dispatch(getStudentInvoices(currentStudent.odooId))
        }
    }, [dispatch, currentStudent?.odooId, currentStudent?.partner, currentStudent?.hasTutor])

    const handleWpLogin = async (e) => {
        e.preventDefault()
        try {
            const { data } = await Axios.get('/api/auth/wp-login', {
                params: { redirect: '/' },
                headers: { auth: localStorage.getItem('jwt') }
            })
            if (data.url) window.open(data.url, '_blank', 'noopener')
        } catch (err) {
            console.error('Error al acceder a WordPress', err)
        }
    }

    const formatCurrency = (amount) => {
        if (amount === null || amount === undefined) return null
        return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(amount)
    }

    const toDate = (utcStr) => new Date(utcStr.replace(' ', 'T') + 'Z')

    const totalSessions = (currentStudent?.odooEnrollments || []).reduce((sum, enr) => sum + (enr.sessions?.length || 0), 0)
    const upcomingSessions = (currentStudent?.odooEnrollments || [])
        .flatMap(enr => (enr.sessions || []).map(ses => ({ enr, ses })))
        .filter(({ ses }) => {
            if (!ses.startDatetime) return false
            const endTime = ses.endDatetime ? toDate(ses.endDatetime).getTime() : toDate(ses.startDatetime).getTime()
            return endTime >= Date.now()
        })
        .sort((a, b) => toDate(a.ses.startDatetime) - toDate(b.ses.startDatetime))
        .slice(0, 5)

    const truncateText = (text, max = 90) => {
        if (!text) return ''
        return text.length > max ? text.slice(0, max).trimEnd() + '…' : text
    }

    const totalAmount = invoices.reduce((sum, inv) => sum + (inv.total || 0), 0)
    const pendingInvoices = invoices.filter(inv => inv.paymentState !== 'paid' && inv.state !== 'posted')
    const paidCount = invoices.filter(inv => inv.paymentState === 'paid' || inv.state === 'posted').length

    return (
        <Container className='my-3 p-5'>
            {dashboardError && (
                <Row>
                    <Col>
                        <Card className='border-0 mb-3'>
                            <Card.Body className='bg-danger bg-opacity-10 rounded-3'>
                                <p className='mb-0 text-danger'><img src="/images/x-icon.svg" alt="x" width={18} className='me-2' />{t('users.odooUnreachable') || 'Error al cargar datos'}</p>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}
            <Row>
                <Col>
                    <Card className='border-0'>
                        <Card.Body className='card dashboard py-5 px-4 text-white shadow'>
                            <h1 className='fw-bolder'>{t('admin.dashboard.welcome')}, {dashboard?.user?.name || clientauth?.name}!</h1>
                            <p className='mt-3 mb-0 fs-6'>{t('admin.dashboard.description')}</p>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {!dashboard && loading ? (<>
                <Row className='g-3 mt-3'>
                    {[1,2,3,4].map(i => (
                        <Col key={i} xs={12} md={6} xl={3} className='my-1'>
                            <Card>
                                <Card.Body className='d-flex p-4 justify-content-between'>
                                    <div className='flex-grow-1'>
                                        <div className='bg-light rounded-3' style={{ height: '12px', width: '60%' }} />
                                        <div className='bg-light rounded-3 mt-2' style={{ height: '24px', width: '40%' }} />
                                    </div>
                                    <div className='bg-light rounded-circle flex-shrink-0' style={{ width: '52px', height: '52px' }} />
                                </Card.Body>
                            </Card>
                        </Col>
                    ))}
                </Row>
                <Row className='mt-4 g-4'>
                    <Col xs={12} xl={8}>
                        <Row className="g-4">
                            <Col xs={12}>
                                <Card className="rounded-4 border-0 shadow-sm">
                                    <Card.Body style={{ padding: '24px' }}>
                                        <div className='bg-light rounded-3' style={{ height: '22px', width: '180px' }} />
                                        <div className='bg-light rounded-3 mt-2' style={{ height: '14px', width: '260px' }} />
                                        <div className='bg-light rounded-3 mt-4' style={{ height: '80px', width: '100%' }} />
                                        <div className='bg-light rounded-3 mt-3' style={{ height: '80px', width: '100%' }} />
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col xs={12}>
                                <Card className="rounded-4">
                                    <Card.Body>
                                        <div className='bg-light rounded-3' style={{ height: '20px', width: '160px' }} />
                                        <div className='bg-light rounded-3 mt-2' style={{ height: '14px', width: '240px' }} />
                                        <div className='row g-3 mt-1'>
                                            {[1,2,3,4,5,6].map(i => (
                                                <div key={i} className='col-12 col-md-6'>
                                                    <div className='bg-light rounded-3 p-3' style={{ height: '70px' }} />
                                                </div>
                                            ))}
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    </Col>
                    <Col xs={12} xl={4}>
                        <Row className="g-4">
                            <Col xs={12}>
                                <Card className="rounded-4">
                                    <Card.Body>
                                        <div className='bg-light rounded-3' style={{ height: '20px', width: '160px' }} />
                                        {[1,2,3,4].map(i => (
                                            <div key={i} className='mt-3 d-flex justify-content-between'>
                                                <div className='bg-light rounded-3' style={{ height: '14px', width: '60px' }} />
                                                <div className='bg-light rounded-3' style={{ height: '14px', width: '100px' }} />
                                            </div>
                                        ))}
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col xs={12}>
                                <Card className="rounded-4">
                                    <Card.Body>
                                        <div className='bg-light rounded-3' style={{ height: '20px', width: '160px' }} />
                                        <div className='bg-light rounded-3 mt-4' style={{ height: '120px', width: '100%' }} />
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col xs={12}>
                                <Card className="rounded-4">
                                    <Card.Body>
                                        <div className='bg-light rounded-3' style={{ height: '20px', width: '180px' }} />
                                        <div className='bg-light rounded-3 mt-4' style={{ height: '200px', width: '100%' }} />
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    </Col>
                </Row>
                </> ) : (
            <><Row className='my-4 dashboard-summary-row'>
                {students.length > 1 && (
                    <Col xs={12}>
                        <Nav variant="tabs" activeKey={activeStudentIdx} onSelect={(k) => setActiveStudentIdx(Number(k))} className='mb-4'>
                            {students.map((s, i) => (
                                <Nav.Item key={s.id}>
                                    <Nav.Link eventKey={i}>{s.name}</Nav.Link>
                                </Nav.Item>
                            ))}
                        </Nav>
                    </Col>
                )}
                <Col xs={12} md={6} xl={3} className='my-1'>
                    <Card className='dashboard-summary-card h-100'>
                        <Card.Body className='d-flex p-4 text-center justify-content-between'>
                            <div className='text-start'>
                                <p className='my-0 fw-bold'>{t('users.status')}</p>
                                {currentStudent
                                    ? <h5 className='fw-bold text-black mb-0'>{currentStudent.active ? t('users.active') : t('users.inactive')}</h5>
                                    : <DataValue value={null} error={'nodata'} loading={loading} />}
                            </div>
                            <div className="icon-circle bg-success bg-opacity-10">
                                <img src="/images/check-dashboard.svg" alt="check"></img>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col xs={12} md={6} xl={3} className='my-1'>
                    <Card className='dashboard-summary-card h-100'>
                        <Card.Body className='d-flex p-4 text-center justify-content-between'>
                            <div className='text-start'>
                                <p className='my-0 fw-bold'>{t('users.course')}</p>
                                <p class="my-0 fs-6 text-black fw-bold"><DataValue value={currentStudent?.courseName || null} loading={loading} /></p>
                            </div>
                            <div className="icon-circle bg-primary bg-opacity-10">
                                <img src="/images/openbook.svg" alt="openbook"></img>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col xs={12} md={6} xl={3} className='my-1'>
                    <Card className='dashboard-summary-card h-100'>
                        <Card.Body className='d-flex p-4 text-center justify-content-between'>
                            <div className='text-start'>
                                <p className='my-0 fw-bold'>{t('users.notifications')}</p>
                                <h5 className='fw-bold text-black mb-0'>
                                    {unreadCount > 0
                                        ? <span className='badge rounded-pill bg-danger' style={{ fontSize: '16px', padding: '6px 14px' }}>{unreadCount}</span>
                                        : (notificationsLoading ? <div className="spinner-border spinner-border-sm text-secondary" role="status" /> : '0')}
                                </h5>
                                {unreadCount > 0 && <p className='my-0 text-muted' style={{ fontSize: '12px' }}>{t('communications.unread')}</p>}
                            </div>
                            <div className="icon-circle bg-warning bg-opacity-10">
                                <img src="/images/exclamation.svg" alt="exclamation" className=''></img>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col xs={12} md={6} xl={3} className='my-1'>
                    <Card className='dashboard-summary-card h-100'>
                        <Card.Body className='d-flex p-4 text-center justify-content-between'>
                            <div className='text-start'>
                                <p className='my-0 fw-bold'>{t('users.group')}</p>
                                <p class="my-0 fs-6 text-black fw-bold"><DataValue value={currentStudent?.group} error={!currentStudent ? 'nodata' : null} loading={loading} /></p>
                            </div>
                            <div className="icon-circle bg-danger bg-opacity-10">
                                <img src="/images/group.svg" alt="group"></img>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
            <Row>
                <Col xs={12} xl={8}>
                    <Row className="g-4">
                        <Col xs={12}>
                            <Card className="rounded-4 border-0 shadow-sm">
                                <Card.Body style={{ padding: '24px' }}>
                                    <div className='d-flex align-items-center gap-2 mb-1'>
                                        <img src="/images/calendar.svg" alt="calendar" width={22} />
                                        <p className='text-black fw-bold mb-0' style={{ fontSize: '22px', color: '#222' }}>{t('users.calendar')}</p>
                                    </div>
                                    <p className='text-muted mb-0' style={{ fontSize: '15px' }}>{t('users.calendardescription')}</p>
                                    <div style={{ height: '24px' }} />
                                    {upcomingSessions.length > 0
                                        ? upcomingSessions.map(({ enr, ses }) => {
                                                const isOnline = enr.modality && enr.modality.toLowerCase().includes('online')
                                                const onlineUrl = ses.onlineUrl
                                                const startDate = toDate(ses.startDatetime)
                                                return (
                                                    <div key={ses.sessionId} className='d-flex align-items-start dashboard-session-item p-3 mb-3 bg-white' style={{ gap: '16px' }}>
                                                        <div className={`d-flex align-items-center justify-content-center rounded-3 flex-shrink-0`}
                                                            style={{ width: '48px', height: '48px', backgroundColor: isOnline ? '#fee2e2' : '#f3f4f6' }}>
                                                            <img src={isOnline ? "/images/camera.svg" : "/images/group.svg"}
                                                                alt={isOnline ? "online" : "presencial"} width={20}
                                                                style={{ filter: isOnline ? 'invert(18%) sepia(94%) saturate(7461%) hue-rotate(0deg) brightness(95%) contrast(118%)' : 'none' }} />
                                                        </div>
                                                        <div className='flex-grow-1' style={{ minWidth: 0 }}>
                                                            <div className='d-flex justify-content-between align-items-start'>
                                                                <div>
                                                                    <p className='fw-semibold mb-0' style={{ fontSize: '18px', color: '#000' }}>{ses.name || enr.courseName}</p>
                                                                    <p className='text-muted mb-0' style={{ fontSize: '14px' }}>
                                                                        {startDate.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })} · {startDate.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                                                                    </p>
                                                                    <p className='mb-0' style={{ fontSize: '14px', color: '#4b5563' }}>{ses.teacher || enr.teacher}</p>
                                                                    {!isOnline && ses.classroom && (
                                                                        <p className='mb-0 mt-1 d-flex align-items-center gap-1' style={{ fontSize: '13px', color: '#dc2626' }}>
                                                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="#dc2626"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>
                                                                            {ses.classroom}
                                                                        </p>
                                                                    )}
                                                                    {isOnline && onlineUrl && (
                                                                        <a href={onlineUrl} target="_blank" rel="noopener noreferrer"
                                                                            className='btn btn-outline-danger btn-sm rounded-pill mt-1 d-inline-flex align-items-center gap-1'>
                                                                            {t('academic.jointoclass')}
                                                                            <img src="/images/external-link.svg" alt="external" width={14} />
                                                                        </a>
                                                                    )}
                                                                </div>
                                                                <span className={`badge rounded-pill flex-shrink-0 ms-2 ${isOnline ? 'bg-danger' : 'bg-primary'}`}
                                                                    style={{ padding: '4px 16px', fontWeight: 500, fontSize: '13px' }}>
                                                                    {isOnline ? 'Online' : 'Presencial'}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )
                                            })
                                        : <p className='fs-6 text-muted'>{t('users.noupcomingclasses')}</p>
                                    }
                                    {totalSessions > 0 && (
                                        <Link to={`/${clientauth?.rol}/academic`} className='btn w-100 border rounded-3 mt-2 fw-semibold'
                                            style={{ height: '44px', color: '#374151', borderColor: '#d1d5db' }}>
                                            {t('academic.fullschedule')}
                                        </Link>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col xs={12} className='mb-4'>
                            <Card className="rounded-4 dashboard-quick-access">
                                <Card.Body>
                                    <div className='d-flex align-items-center gap-2'>
                                        <img src="/images/external-link.svg" alt="external-link" width={18} />
                                        <p className='text-black fw-semibold fs-6 mb-0'>{t('users.quickaccess')}</p>
                                    </div>
                                    <p className='mt-2 fs-6 text-muted'>{t('users.quickaccessdescription')}</p>
                                    <Row className='g-3 mt-1'>
                                        <Col xs={12} md={6}>
                                            <a href="#" onClick={handleWpLogin} className='text-decoration-none'>
                                                <Card className='rounded-4 h-100 quick-access-card'>
                                                    <Card.Body>
                                                        <div className='d-flex justify-content-between align-items-start'>
                                                            <div className='d-flex gap-3'>
                                                                <div className='icon-box'>
                                                                    <img src="/images/openbook.svg" alt="openbook" width={18} />
                                                                </div>
                                                                <div>
                                                                    <p className='fw-semibold text-black mb-1 fs-6'>{t('users.virtualcampus')}</p>
                                                                    <p className='text-muted mb-0'>{t('users.virtualcampusdescription')}</p>
                                                                </div>
                                                            </div>
                                                            <img src="/images/external-link.svg" alt="external" width={16} />
                                                        </div>
                                                    </Card.Body>
                                                </Card>
                                            </a>
                                        </Col>
                                        <Col xs={12} md={6}>
                                            <Card className='rounded-4 h-100 quick-access-card'>
                                                <Card.Body>
                                                    <div className='d-flex justify-content-between align-items-start'>
                                                        <div className='d-flex gap-3'>
                                                            <div className='icon-box'>
                                                                <img src="/images/openbook.svg" alt="openbook" width={18} />
                                                            </div>
                                                            <div>
                                                                <p className='fw-semibold text-black mb-1 fs-6'>{t('users.onlineclasses')}</p>
                                                                <p className='text-muted mb-0'>{t('users.onlineclassesdescription')}</p>
                                                            </div>
                                                        </div>
                                                        <img src="/images/external-link.svg" alt="external" width={16} />
                                                    </div>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                        <Col xs={12} md={6}>
                                            <Card className='rounded-4 h-100 quick-access-card'>
                                                <Card.Body>
                                                    <div className='d-flex justify-content-between align-items-start'>
                                                        <div className='d-flex gap-3'>
                                                            <div className='icon-box'>
                                                                <img src="/images/openbook.svg" alt="openbook" width={18} />
                                                            </div>
                                                            <div>
                                                                <p className='fw-semibold text-black mb-1 fs-6'>{t('users.library')}</p>
                                                                <p className='text-muted mb-0'>{t('users.librarydescription')}</p>
                                                            </div>
                                                        </div>
                                                        <img src="/images/external-link.svg" alt="external" width={16} />
                                                    </div>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                        <Col xs={12} md={6}>
                                            <Card className='rounded-4 h-100 quick-access-card'>
                                                <Card.Body>
                                                    <div className='d-flex justify-content-between align-items-start'>
                                                        <div className='d-flex gap-3'>
                                                            <div className='icon-box'>
                                                                <img src="/images/document.svg" alt="document" width={18} />
                                                            </div>
                                                            <div>
                                                                <p className='fw-semibold text-black mb-1 fs-6'>{t('users.documentation')}</p>
                                                                <p className='text-muted mb-0'>{t('users.documentationdescription')}</p>
                                                            </div>
                                                        </div>
                                                        <img src="/images/external-link.svg" alt="external" width={16} />
                                                    </div>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                        <Col xs={12} md={6}>
                                            <Card className='rounded-4 h-100 quick-access-card'>
                                                <Card.Body>
                                                    <div className='d-flex justify-content-between align-items-start'>
                                                        <div className='d-flex gap-3'>
                                                            <div className='icon-box'>
                                                                <img src="/images/users.svg" alt="users" width={18} />
                                                            </div>
                                                            <div>
                                                                <p className='fw-semibold text-black mb-1 fs-6'>{t('users.tutorship')}</p>
                                                                <p className='text-muted mb-0'>{t('users.tutorshipdescription')}</p>
                                                            </div>
                                                        </div>
                                                        <img src="/images/external-link.svg" alt="external" width={16} />
                                                    </div>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                        <Col xs={12} md={6}>
                                            <Card className='rounded-4 h-100 quick-access-card'>
                                                <Card.Body>
                                                    <div className='d-flex justify-content-between align-items-start'>
                                                        <div className='d-flex gap-3'>
                                                            <div className='icon-box'>
                                                                <img src="/images/camera.svg" alt="camera" width={18} />
                                                            </div>
                                                            <div>
                                                                <p className='fw-semibold text-black mb-1 fs-6'>{t('users.videolibrary')}</p>
                                                                <p className='text-muted mb-0'>{t('users.videolibrarydescription')}</p>
                                                            </div>
                                                        </div>
                                                        <img src="/images/external-link.svg" alt="external" width={16} />
                                                    </div>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </Col>
                <Col xs={12} xl={4}>
                    <Row className="g-4">
                        <Col xs={12}>
                            <Card className="rounded-4 dashboard-info-card">
                                <Card.Body>
                                    <div style={{ minHeight: '260px' }}>
                                        <div className='d-flex align-items-center gap-2'>
                                            <img src="/images/openbook.svg" alt="openbook" width={18} />
                                            <p className='text-black fw-semibold fs-6 mb-0'>{t('users.announcements')}</p>
                                        </div>
                                        <div className='mt-3'>
                                            <p className='my-0 fs-6'>{t('users.course')}</p>
                                            <p className='my-0 fs-6 text-black fw-bold'>
                                                <DataValue value={currentStudent?.courseName || null}
                                                    error={!currentStudent ? 'nodata' : null} loading={loading} />
                                            </p>
                                        </div>
                                        <div className='mt-3'>
                                            <p className='my-0 fs-6'>{t('users.group')}</p>
                                            <p className='my-0 fs-6 text-black fw-bold'><DataValue value={currentStudent?.group} error={!currentStudent ? 'nodata' : null} loading={loading} /></p>
                                        </div>
                                        <div className='mt-3'>
                                            <p className='my-0 fs-6'>{t('users.modality')}</p>
                                            <p className='my-0 fs-6 text-black fw-bold'><DataValue value={currentStudent?.modality} error={!currentStudent ? 'nodata' : null} loading={loading} /></p>
                                        </div>
                                        <div className='mt-3'>
                                            <p className='my-0 fs-6'>{t('users.tutor')}</p>
                                            <p className='my-0 fs-6 text-black fw-bold'><DataValue value={currentStudent?.tutor} error={!currentStudent ? 'nodata' : null} loading={loading} /></p>
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                        {!currentStudent?.hasTutor && (
                        <Col xs={12}>
                            <Card className="rounded-4 dashboard-economics-card">
                                <Card.Body>
                                    <div style={{ minHeight: '180px' }}>
                                        <div className='d-flex align-items-center gap-2'>
                                            <img src="/images/creditcard.svg" alt="creditcard"></img>
                                            <p className='text-black fw-semibold fs-6 mb-0'>{t('users.economics')}</p>
                                        </div>
                                        <div className='mt-3'>
                                            <div className='d-flex justify-content-between mb-2'>
                                                <span className='text-muted'>{t('administration.totalamount')}</span>
                                                <span className='fw-semibold text-black'>{invoicesLoading ? '—' : (totalAmount ? formatCurrency(totalAmount) : '—')}</span>
                                            </div>
                                            <div className='d-flex justify-content-between mb-2'>
                                                <span className='text-muted'>{t('administration.paidfees')}</span>
                                                <span className='fw-semibold text-black'>{invoicesLoading ? '—' : `${paidCount}`}</span>
                                            </div>
                                            <div className='d-flex justify-content-between mb-2'>
                                                <span className='text-muted'>{t('administration.pendingfees')}</span>
                                                <span className='fw-semibold text-black'>{invoicesLoading ? '—' : `${pendingInvoices.length}`}</span>
                                            </div>
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                        )}
                        <Col xs={12}>
                            <Card className="rounded-4">
                                <Card.Body>
                                    <div style={{ minHeight: '280px' }}>
                                        <div className='d-flex align-items-center gap-2'>
                                            <img src="/images/exclamation.svg" alt="exclamation" width={18} />
                                            <p className='text-black fw-semibold fs-6 mb-0'>{t('users.recentnotifications')}</p>
                                        </div>
                                        {notificationsLoading ? (
                                            <div className='mt-3'>
                                                {[1, 2, 3].map(i => (
                                                    <div key={i} className='border rounded-3 p-3 mb-2'>
                                                        <div className='bg-light rounded-3' style={{ height: '14px', width: '70%' }} />
                                                        <div className='bg-light rounded-3 mt-2' style={{ height: '12px', width: '90%' }} />
                                                    </div>
                                                ))}
                                            </div>
                                        ) : notifications.length === 0 ? (
                                            <p className='text-muted text-center py-4 mb-0' style={{ fontSize: '14px' }}>{t('users.norecentnotifications')}</p>
                                        ) : (
                                            <>
                                                <div className='mt-3'>
                                                    {notifications.slice(0, 3).map(n => (
                                                        <div key={n._id} className='d-flex align-items-start gap-3 border rounded-3 p-3 mb-2'
                                                            style={{ backgroundColor: n.read ? '#fff' : '#fff7ed', borderColor: n.read ? '#e5e7eb' : '#fdba74' }}>
                                                            <div className='d-flex align-items-center justify-content-center rounded-circle flex-shrink-0'
                                                                style={{ width: '36px', height: '36px', backgroundColor: n.read ? '#f3f4f6' : '#ffedd5' }}>
                                                                <img src="/images/exclamation.svg" alt="notification" width={16}
                                                                    style={{ filter: n.read ? 'none' : 'invert(48%) sepia(79%) saturate(2476%) hue-rotate(346deg) brightness(100%) contrast(97%)' }} />
                                                            </div>
                                                            <div className='flex-grow-1' style={{ minWidth: 0 }}>
                                                                <p className='fw-semibold mb-1 text-black' style={{ fontSize: '14px' }}>{n.subject}</p>
                                                                <p className='text-muted mb-1' style={{ fontSize: '13px' }}>{truncateText(n.message)}</p>
                                                                <p className='mb-0 text-muted' style={{ fontSize: '12px' }}>
                                                                    {new Date(n.createdAt).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' })}
                                                                    {!n.read && <span className='badge rounded-pill bg-danger ms-2' style={{ fontSize: '10px' }}>{t('communications.new')}</span>}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                                <Link to={`/${clientauth?.rol}/communications`} className='btn w-100 border rounded-3 mt-2 fw-semibold'
                                                    style={{ height: '44px', color: '#374151', borderColor: '#d1d5db' }}>
                                                    {t('users.viewallnotifications')}
                                                </Link>
                                            </>
                                        )}
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </Col>
            </Row>
            </>)}
        </Container>
    )
}